import { useContext, useEffect, useMemo } from 'react';
import { generatePath, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { isAxiosError } from 'axios';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useRegisterChatPageContext } from '@trinity/chat-components/components/Chat/hooks/useRegisterPageContext.js';
import { path } from '@/consts';
import { WorkspaceBreadcrumbsContext } from '@/containers/WorkspaceObject/Context';
import { ChatContextType } from '@/enums/chat';
import type { BrandInsightsPageContext } from '@/types';
import { useFileTagsEvents } from '@/hooks/useFileTagsEvents';
import DownloadFileContainer from '../Workspace.File/DownloadFileContainer';
import {
  WorkspaceFileStateContext,
  WorkspaceFilePreviewContext,
  WorkspaceFilePreviewReloadContext,
  WorkspaceFilePreviewLoadingContext,
} from './Context';
import SummaryContainer from './SummaryContainer';
import GroupFileTagsContainer from '../GroupTags/GroupFileTagsContainer';
import { TagGeneratingFilesContext } from '@trinity/chat-components/containers/Workspace/context.js';

type Props =
  ChildrenProps;

type RouteParams = {
  fileId: string;
};

const usePageParams = () => {
  const params = useParams<RouteParams>();
  return useMemo(() => ({
    fileId: +params.fileId,
  }), [params.fileId]);
};

export const WorkspaceFilePreviewContainer = (props: Props) => {
  const params = usePageParams();
  const navigate = useNavigate();

  const apiClient = useChatApiClient();

  const query = useQuery({
    queryKey: [
      `get:workspaces/files`,
      params.fileId,
    ],
    queryFn: () => {
      return apiClient.workspaces.fetchWorkspaceFile({
        fileId: params.fileId,
      });
    },
  });

  useEffect(() => {
    if (query.isError && isAxiosError(query.error) && query.error.status === 404) {
      navigate(path.Workspaces.FileNotFound, { replace: true });
    }
  }, [query.isError, query.error, navigate]);

  const documentContext: BrandInsightsPageContext.Document = useMemo(() => {
    return {
      type: ChatContextType.Document,
      data: {
        documentId: params.fileId,
      },
      metadata: {
        name: query.data?.file?.name,
      },
    };
  }, [query.data?.file?.name, params.fileId]);

  const register = useRegisterChatPageContext();

  useEffect(() => {
    if (!documentContext) return;

    if (documentContext.data.documentId && documentContext.metadata.name) {
      register(documentContext);
    }
  }, [register, documentContext]);

  const state = useMemo(() => ({
    capabilities: query.data?.capabilities,
    file: query.data?.file,
    meta: query.data?.meta,
    object: query.data?.object,
    transcript: query.data?.transcript,
  }), [query.data]);

  const tagGeneratingContext = useContext(TagGeneratingFilesContext);

  // Subscribe to WebSocket events if this file is generating tags
  const generatingFileIds = useMemo(() => {
    if (!tagGeneratingContext || !params.fileId || !query.data) return [];
    if (!query.data.fileVersion.generateTags) return [];
    return tagGeneratingContext.isGenerating(params.fileId) ? [params.fileId] : [];
  }, [tagGeneratingContext, params.fileId, query.data]);
  useFileTagsEvents(generatingFileIds);

  // Remove file from generating state when tags appear in the data
  // This is a fallback in case WebSocket events don't fire or are missed
  useEffect(() => {
    if (!tagGeneratingContext || !query.data || !params.fileId) return;

    const fileId = params.fileId;
    if (tagGeneratingContext.isGenerating(fileId) && query.data.tags && query.data.tags.length > 0) {
      tagGeneratingContext.removeGeneratingFile(fileId);
    }
  }, [query.data?.tags, tagGeneratingContext, params.fileId]);

  // Guard against missing essential data - don't render children if file or object is missing
  if (query.isFetched && (!query.data?.file || !query.data?.object)) {
    const e = query.error as AxiosError<IWorkspaceObjectId, IWorkspaceObjectId>;
    const objectData = e?.response?.data;
    const forbidden = query.isError && e.status === 403;
    const hasObjectId = query.data !== null && typeof query.data === 'object' && !!objectData?.objectId;

    return forbidden && hasObjectId
      ? <Navigate to={generatePath(path.Access.Request, query.data)} />
      : <Navigate to={path.Workspaces.FileNotFound} />;
  }

  return (
    <WorkspaceFilePreviewReloadContext.Provider value={query.refetch}>
      <WorkspaceFileStateContext.Provider value={state}>
        <WorkspaceFilePreviewContext.Provider value={query.data?.preview}>
          <WorkspaceFilePreviewLoadingContext.Provider value={query.isLoading}>
            <WorkspaceBreadcrumbsContext.Provider value={query.data?.breadcrumbs || []}>
              <GroupFileTagsContainer fileId={params.fileId}>
                <SummaryContainer fileId={params.fileId}>
                  <DownloadFileContainer fileId={params.fileId} workspaceId={query.data?.object?.workspaceId}>
                    {props.children}
                  </DownloadFileContainer>
                </SummaryContainer>
              </GroupFileTagsContainer>
            </WorkspaceBreadcrumbsContext.Provider>
          </WorkspaceFilePreviewLoadingContext.Provider>
        </WorkspaceFilePreviewContext.Provider>
      </WorkspaceFileStateContext.Provider>
    </WorkspaceFilePreviewReloadContext.Provider>
  );
};